/**
 * Report alert selection — the pure, non-rendering half of the former
 * ReportAlertBanner (Reader Polish Prompt 3).
 *
 * 2026-10-13 (William: "i dont want any banners on report pages"): the report
 * page no longer mounts ReportAlertBanner. The conditions it detected still
 * matter, so the pickers live here, free of JSX, and feed the ONE compact
 * status chip in ReportHeaderStrip (see `reportStatusChip.ts`).
 * ReportAlertBanner re-exports them so existing imports keep working.
 *
 * Priority order inside `pickReportAlert` (highest first):
 *   1. degraded — narrative pipeline failed; user must refresh.
 *   2. high-severity unresolved evidence conflicts.
 *   3. input drift — `pickReportRefreshAlert` (SED, supply chain, financial
 *      inputs, pinned pathway comparison).
 *   4. citation-rewrite — out-of-bounds [Ref N] tokens were scrubbed.
 *   5. first-run — opt-in only.
 *
 * The "critical-doc-missing" and "unresolved-finding" banners were removed
 * earlier (see git history of ReportAlertBanner.tsx); their counts surface
 * through the header's Review quality details badge.
 *
 * Pure / synchronous. Callers own every CTA handler.
 */
import type { SiteRagReport } from '../../../types/siteRag';
import type { SiteEntity } from '../../../types/site';
import type { SiteEvidenceDocument } from '../../../types/siteEvidence';
import { computeLiveUnresolvedConflicts } from '../../../services/evidenceConflict';
import { getSupplyChainFreshness } from '../../../lib/supplyChainReportFreshness';
import {
  financialDriftSourceLabel,
  getFinancialReportFreshness,
} from '../../../lib/financialReportFreshness';
import { getPathwayComparisonFreshness } from '../../../lib/pathwayComparisonFreshness';
import { classifyNarrativeFailure } from '../../../lib/classifyNarrativeFailure';
import { reportIntentIsCurrent } from '../../../lib/pathToGo/reportPlan';

export type ReportAlertKind =
  | 'degraded'
  | 'high-conflicts'
  | 'sed-drift'
  | 'project-intent-drift'
  | 'supply-chain-drift'
  | 'pathway-comparison-stale'
  | 'financial-inputs-changed'
  | 'citation-rewrite'
  | 'first-run';

/** Live site context the pickers compare the report against. */
export type ReportAlertSite =
  & Pick<SiteEntity, 'sedVersion' | 'supplyChainPlan' | 'financialAssumptions' | 'projectIntent' | 'pinnedPathwayComparisonId'>
  & { sed?: SiteEvidenceDocument | null };

export interface ReportAlertInputs {
  report: SiteRagReport;
  /**
   * CB2 — `sed` is the LIVE site SED (current state, not the gen-time
   * snapshot). When present, the high-conflicts count derives from this SED
   * so resolutions in the editor disappear immediately. Absent (legacy
   * reports / deleted sites) → falls back to `report.unresolvedConflicts`.
   *
   * `SiteEntity` itself doesn't carry the SED payload (it's loaded lazily by
   * `sedLoader`), so the parent attaches it via an intersection here rather
   * than via a SiteEntity field.
   */
  site?: ReportAlertSite | null;
  /** Force the first-run case on. Defaults to false; no report-page caller
   *  passes it, so the first-run case never fires from the report surface. */
  firstRun?: boolean;
  /** Refresh / regen handler used by the degraded case. */
  onRefreshNarrative?: () => void;
  /** Open the evidence conflicts surface for the conflicts case. */
  onOpenSedEditor?: () => void;
  /** Rerun handler for the drift cases. */
  onRerun?: () => void;
}

export interface ResolvedAlert {
  kind: ReportAlertKind;
  /** Short bold label rendered first. */
  label: string;
  /** Body sentence. */
  detail?: string;
  /** Optional CTA — text + handler. */
  ctaLabel?: string;
  ctaHandler?: () => void;
  /** RAG severity → drives the non-icon treatment. */
  severity: 'red' | 'amber' | 'blue';
  /** Optional href anchor — when set, the CTA renders as an anchor link
   *  rather than firing a handler. No active case uses it today. */
  href?: string;
}

/** True when the report assembled without its AI narrative. */
export function isNarrativeDegraded(report: SiteRagReport): boolean {
  return Boolean(report.degradedMode || persistedDegradedReason(report));
}

function persistedDegradedReason(report: SiteRagReport) {
  const persistedReasons = report.reportRevision?.deliveryDecision?.reasons;
  return Array.isArray(persistedReasons)
    ? persistedReasons.find((reason) => reason?.code === 'degraded')
    : undefined;
}

/**
 * CB2 — the high-severity unresolved conflict count, read from the LIVE site
 * SED when one is attached (so resolutions remove the signal immediately),
 * otherwise from the count stamped at generation.
 */
export function liveHighSeverityConflicts(
  report: SiteRagReport,
  site: ReportAlertInputs['site'],
): { live: number; stamped: number } {
  const stamped = report.unresolvedConflicts?.bySeverity.high ?? 0;
  const liveCounts = site?.sed !== undefined
    ? computeLiveUnresolvedConflicts(site?.sed ?? null)
    : null;
  return { live: liveCounts ? liveCounts.bySeverity.high : stamped, stamped };
}

/**
 * Resolve the single highest-priority alert for the current state, or null.
 * Exposed for tests so the picker logic is asserted independently of any JSX.
 */
export function pickReportAlert(props: ReportAlertInputs): ResolvedAlert | null {
  const { report, site, firstRun, onRefreshNarrative, onOpenSedEditor } = props;

  // 1. Degraded narrative — the report assembled without AI prose.
  //    SSM-2 — clarify that scores are still derived from provider +
  //    (when applicable) authoritative doc evidence; the executive
  //    summary is the template.
  const degradedReason = persistedDegradedReason(report);
  if (report.degradedMode || degradedReason) {
    const baseReason = report.degradedReason
      ? classifyNarrativeFailure({ error: report.degradedReason }).userMessage
      : degradedReason?.message ?? 'AI narrative pipeline failed at generation time.';
    const detail =
      `AI narrative unavailable – scores derived from deterministic provider data` +
      ` + authoritative doc evidence (when present). Executive Summary is a template.` +
      ` ${baseReason}`;
    return {
      kind: 'degraded',
      label: 'Narrative unavailable',
      detail,
      ctaLabel: onRefreshNarrative ? 'Refresh narrative' : undefined,
      ctaHandler: onRefreshNarrative,
      severity: 'red',
    };
  }

  // 2. High-severity unresolved evidence conflicts (CB2 live count; the
  //    stamped snapshot stays as the audit trail in Data Quality Notes).
  const { live: liveHigh, stamped: stampedHigh } = liveHighSeverityConflicts(report, site);
  if (liveHigh > 0) {
    // EDX-10 — "N of M … (Δ resolved since this report ran)" so the
    // resolution lineage is obvious. stamped == 0 or stamped < live falls
    // back to "N of N" (never "0 of N").
    const denom = stampedHigh > 0 && stampedHigh >= liveHigh ? stampedHigh : liveHigh;
    const resolvedSince = stampedHigh > liveHigh ? stampedHigh - liveHigh : 0;
    const resolvedNote = resolvedSince > 0
      ? ` (${resolvedSince} resolved since this report ran).`
      : '.';
    return {
      kind: 'high-conflicts',
      label: `${liveHigh} of ${denom} high-severity conflict${denom === 1 ? '' : 's'} still need review`,
      detail: `Findings drawn from conflicting fields should be treated as preliminary${resolvedNote} Click Review to resolve.`,
      ctaLabel: onOpenSedEditor ? 'Review conflicts →' : undefined,
      ctaHandler: onOpenSedEditor,
      severity: 'red',
    };
  }
  // CB2 edge case: stamped > 0 but live === 0 — every conflict was resolved
  // post-generation; the condition above already gates the alert away.

  const refreshAlert = pickReportRefreshAlert(props);
  if (refreshAlert) return refreshAlert;

  // 4. Citation rewrite — the citation hard-gate's strict-retry path
  // didn't catch every out-of-bounds [Ref N], so the rewriter REMOVED
  // the survivors (Redhead wave 3 — never a literal [Ref ?] in prose).
  if (report.citationsRewritten) {
    const n = report.citationsRewrittenCount ?? 0;
    // "See Data Quality Notes" only when that appendix will actually render —
    // it exists solely when crossValidationFindings is non-empty.
    const hasQualityNotes = (report.crossValidationFindings?.length ?? 0) > 0;
    return {
      kind: 'citation-rewrite',
      label: 'Some citations were removed',
      detail: `${n} inline [Ref N] token${n === 1 ? '' : 's'} pointed beyond the appendix and ${n === 1 ? 'was' : 'were'} removed from the narrative.${hasQualityNotes ? ' See Data Quality Notes.' : ''}`,
      severity: 'amber',
    };
  }

  // 5. First-run — opt-in via prop. Blue / info treatment.
  if (firstRun) {
    return {
      kind: 'first-run',
      label: 'First report on this site',
      detail: 'We collected site evidence automatically – review the body and add documents to strengthen reruns.',
      severity: 'blue',
    };
  }

  return null;
}

/**
 * Input-drift criteria (SED, supply chain, financial inputs, pinned pathway
 * comparison). The copy names the report header's Rerun action — never a
 * "Regenerate" call to action (William, 2026-10-13).
 */
export function pickReportRefreshAlert(
  { report, site, onRerun }: Pick<ReportAlertInputs, 'report' | 'site' | 'onRerun'>,
): ResolvedAlert | null {
  // 3. SED drift — site SED has been rebuilt since this report ran.
  //    SED_Architecture_Unification SA7 — reporting "v1 → v3" rather than
  //    "stale" because a rebuild bumps site.sedVersion and a rerun re-pins
  //    sedVersionUsed.
  const usedV = report.sedVersionUsed;
  const currentV = site?.sedVersion;
  if (usedV != null && currentV != null && currentV > usedV) {
    return {
      kind: 'sed-drift',
      label: 'Site SED updated',
      detail: `This report was generated against v${usedV}; site SED is now v${currentV}. Rerun the report to incorporate the new evidence.`,
      ctaLabel: onRerun ? 'Rerun' : undefined,
      ctaHandler: onRerun,
      severity: 'amber',
    };
  }

  // 3b. Supply-chain drift — the live BoM/supplier/quote plan changed
  // after this supply-chain-risk report was generated.
  const scFreshness = getSupplyChainFreshness(report, site);
  if (scFreshness.stale) {
    return {
      kind: 'supply-chain-drift',
      label: 'Supply-chain inputs changed',
      detail: 'BoM/supplier/quote data has changed since this report ran. Rerun the report to refresh procurement and COD risk signals.',
      ctaLabel: onRerun ? 'Rerun' : undefined,
      ctaHandler: onRerun,
      severity: 'amber',
    };
  }

  // 3b-fin. FINANCIAL INPUTS CHANGED — the model, the phases or the equipment
  // plan moved after this report's economics were computed. The Financial
  // verdict is a SNAPSHOT: the engine runs once at generation and nothing
  // re-runs it, so without this signal an edited CAPEX left every surface
  // showing a band derived from numbers the site no longer holds.
  const finFreshness = getFinancialReportFreshness(report, site);
  if (finFreshness.stale) {
    const what = finFreshness.changed.map(financialDriftSourceLabel);
    const list = what.length === 1
      ? what[0]
      : `${what.slice(0, -1).join(', ')} and ${what[what.length - 1]}`;
    const whenSuffix = finFreshness.staleDays
      ? ` (${finFreshness.staleDays} day${finFreshness.staleDays === 1 ? '' : 's'} ago)`
      : '';
    // TWO DIFFERENT TRUTHS. A re-stamp recomputes the deterministic figures on
    // every financial-assumptions save, but it cannot rewrite the generated
    // analysis. Name the half that is actually stale.
    const detail = finFreshness.figuresCurrent
      ? `The figures below have been recomputed since ${list} changed${whenSuffix}`
        + ', so the verdict, LCOE, NPV and IRR are current. The written analysis still '
        + 'describes the earlier model – rerun the report to bring the commentary in line.'
      : `This report's economics were computed before ${list} changed${whenSuffix}`
        + '. The verdict, LCOE, NPV and IRR shown here are from the earlier model. Rerun the report to recompute them.';
    return {
      kind: 'financial-inputs-changed',
      label: finFreshness.figuresCurrent ? 'Analysis text is behind the model' : 'Financial inputs changed',
      detail,
      ctaLabel: onRerun ? 'Rerun' : undefined,
      ctaHandler: onRerun,
      severity: 'amber',
    };
  }

  // 3c. Pathway comparison stale pin — BoM / FA edited after pinned comparison.
  const pcFreshness = getPathwayComparisonFreshness(report, site);
  if (pcFreshness.stale) {
    const days = pcFreshness.staleDays ?? 1;
    return {
      kind: 'pathway-comparison-stale',
      label: 'Pathway comparison inputs changed',
      detail: `Pathway comparison is ${days} day${days === 1 ? '' : 's'} stale relative to inputs – rerun the report to refresh it.`,
      ctaLabel: onRerun ? 'Rerun' : undefined,
      ctaHandler: onRerun,
      severity: 'amber',
    };
  }

  // 3d. Project-intent drift — site's current projectIntent changed after this report revision was generated (`DC-X-006`).
  if (site && report.projectIntentAtGen != null && !reportIntentIsCurrent(report, site.projectIntent)) {
    return {
      kind: 'project-intent-drift',
      label: 'Project intent changed',
      detail: 'Site project intent has changed since this report revision was generated. This historical revision remains immutable; rerun the report to reflect the updated project intent.',
      ctaLabel: onRerun ? 'Rerun' : undefined,
      ctaHandler: onRerun,
      severity: 'amber',
    };
  }

  return null;
}
